/**
 * Coach chat client helpers (web + mobile).
 */

/** Max chars for a single coach user message (client cap; server sanitizes further). */
export const COACH_USER_MESSAGE_MAX_CHARS = 2000;

/** True when the coach API is disabled for this environment (503). */
export function isCoachChatDisabledStatus(status) {
  return status === 503;
}

/**
 * Probe whether coach chat is enabled on the server.
 * @param {(url: string, init?: RequestInit) => Promise<Response>} fetchFn
 * @returns {Promise<boolean>}
 */
export async function probeCoachChatEnabled(fetchFn) {
  try {
    const res = await fetchFn('/api/coach/threads', { method: 'GET' });
    return !isCoachChatDisabledStatus(res.status);
  } catch {
    return true;
  }
}

/**
 * @param {Response} res
 * @param {Record<string, unknown>} [body]
 */
export function isCoachQuotaExceeded(res, body) {
  return (res.status === 403 || res.status === 429) && body?.code === 'QUOTA_EXCEEDED';
}

/** Transport rate limit (express-rate-limit), not monthly coach quota. */
export function isCoachTransportRateLimited(res, body) {
  return res.status === 429 && body?.code !== 'QUOTA_EXCEEDED';
}

/** True when rider compact exceeded the coach token budget (413). */
export function isCoachContextTooLarge(res, body) {
  return res.status === 413 && body?.code === 'CONTEXT_TOO_LARGE';
}

/**
 * User-facing send failure copy. Token-budget 413s stay generic - no LLM jargon.
 * @param {{ status: number }} res
 * @param {unknown} body
 * @param {string} fallback
 */
export function resolveCoachSendErrorMessage(res, body, fallback) {
  if (isCoachContextTooLarge(res, body)) return fallback;
  return parseCoachApiErrorMessage(body, fallback);
}

/**
 * Normalise API error bodies (`message` top-level or `{ error: { message } }`).
 * @param {unknown} body
 * @param {string} [fallback]
 */
export function parseCoachApiErrorMessage(body, fallback = '') {
  if (!body || typeof body !== 'object') return fallback;
  const record = /** @type {Record<string, unknown>} */ (body);
  if (typeof record.message === 'string' && record.message.trim()) {
    return record.message.trim();
  }
  const nested = record.error;
  if (typeof nested === 'string' && nested.trim()) return nested.trim();
  if (nested && typeof nested === 'object') {
    const msg = /** @type {{ message?: string }} */ (nested).message;
    if (typeof msg === 'string' && msg.trim()) return msg.trim();
  }
  return fallback;
}

/** Production 404 when DELETE route is not deployed (Express catch-all). */
export function isCoachDeleteRouteMissing(res, body) {
  if (res.status !== 404) return false;
  const msg = parseCoachApiErrorMessage(body, '');
  return /Cannot DELETE \/api\/coach\/threads/i.test(msg);
}

/**
 * User-facing delete failure copy.
 * @param {Response} res
 * @param {unknown} body
 * @param {{ generic: string; routeMissing: string; threadNotFound: string }} labels
 */
export function resolveCoachDeleteErrorMessage(res, body, labels) {
  if (isCoachDeleteRouteMissing(res, body)) return labels.routeMissing;
  const parsed = parseCoachApiErrorMessage(body, '');
  if (res.status === 404) {
    return parsed || labels.threadNotFound;
  }
  return parsed || labels.generic;
}
